/**
 * Chat Service — Frontend API service for AI chat
 *
 * Sends messages to the v3 chat endpoint and returns result objects.
 * Follows the processingService.js / invitationService.js pattern.
 */

import { getAuthHeaders } from "./apiUtils";
import { ServerURI } from "../globals";

/**
 * Send a chat message to the AI backend.
 *
 * @param {Object} params
 * @param {string} params.message        — User message text
 * @param {string} [params.documentId]   — Current document ID
 * @param {string} [params.patientId]    — Current patient ID
 * @param {Object} [params.documentData] — Lightweight document metadata
 * @param {Array}  [params.history]      — Prior conversation turns
 * @returns {Promise<{success: boolean, message?: string, functionCalled?: string, error?: string}>}
 */
export async function sendChatMessage({ message, documentId, patientId, documentData, pageContext, patientName, history, signal }) {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${ServerURI}/api/v3/chat`, {
      method: "POST",
      signal,
      headers,
      body: JSON.stringify({
        message,
        documentId: documentId || null,
        patientId: patientId || null,
        documentData: documentData || null,
        pageContext: pageContext || null,
        patientName: patientName || null,
        history: history || [],
        historyPatientId: patientId || null,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        message: data.message,
        functionCalled: data.functionCalled || null,
        searchResults: data.searchResults || null,
        historyCoverage: data.historyCoverage || null,
      };
    }

    if (response.status === 429) {
      return { success: false, error: "AI assistant is busy. Please try again in a moment." };
    }

    if (response.status === 504) {
      return { success: false, error: "Request timed out. Please try again." };
    }

    const errorData = await response.json().catch(() => ({}));
    return { success: false, error: errorData.error || `Server error (${response.status})` };
  } catch (err) {
    if (err.name === 'AbortError') return { success: false, cancelled: true };
    console.error("[chatService] Exception:", err);
    return { success: false, error: "Failed to reach the server. Check your connection." };
  }
}
